import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { api } from "@/services/api";
import { UserInitializer } from "@/components/UserInitializer";

interface Auth0ContextType {
  user: any;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithRedirect: (returnTo?: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  testAuth0Api: () => Promise<any>;
}

const Auth0Context = createContext<Auth0ContextType | undefined>(undefined);

export function Auth0ContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    user,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect: auth0Login,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [isAppLoading, setIsAppLoading] = useState(true);
  const [showInitializer, setShowInitializer] = useState(false);
  const [hasCheckedInit, setHasCheckedInit] = useState(false);

  // Set up API token callback and check initialization when user is authenticated
  useEffect(() => {
    const setupAndCheckInit = async () => {
      if (!isAuthenticated || !user || hasCheckedInit) {
        return;
      }

      console.log(
        "Auth0Context: Setting up token callback and checking initialization..."
      );

      // First, set up the token callback
      const getAccessToken = async (): Promise<string | null> => {
        if (!isAuthenticated) return null;
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            },
          });
        } catch (error) {
          console.error("Error getting access token:", error);
          return null;
        }
      };

      api.setAuth0TokenCallback(getAccessToken);

      // Wait for token to actually be available before making API calls
      console.log("Auth0Context: Waiting for valid token...");
      let token = null;
      let attempts = 0;
      const maxAttempts = 10;

      while (!token && attempts < maxAttempts) {
        try {
          token = await getAccessTokenSilently({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            },
          });
          if (token) {
            console.log("Auth0Context: Token acquired successfully");
            break;
          }
        } catch (error) {
          console.log(
            `Auth0Context: Token attempt ${attempts + 1} failed:`,
            error
          );
          attempts++;
          // Wait 200ms between attempts
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      if (!token) {
        console.error(
          "Auth0Context: Failed to acquire token after",
          maxAttempts,
          "attempts"
        );
        return;
      }

      // Now check initialization with token available
      console.log("Auth0Context: Checking user initialization...");
      setHasCheckedInit(true);

      try {
        const response = await api.isUserInitialized();
        console.log("Auth0Context: Initialization check response:", response);

        if (response.success && !response.data?.initialized) {
          console.log(
            "Auth0Context: User needs initialization, showing initializer"
          );
          setShowInitializer(true);
        } else if (response.success && response.data?.initialized) {
          console.log("Auth0Context: User already initialized");
          setShowInitializer(false);
        } else {
          console.log(
            "Auth0Context: Check failed, showing initializer to be safe"
          );
          setShowInitializer(true);
        }
      } catch (error: any) {
        console.error("Auth0Context: Error checking initialization:", error);

        if (error.response?.status === 404) {
          console.log("Auth0Context: Endpoint not found, assuming initialized");
          setShowInitializer(false);
        } else {
          console.log(
            "Auth0Context: Error occurred, showing initializer to be safe"
          );
          setShowInitializer(true);
        }
      }
    };

    setupAndCheckInit();
  }, [isAuthenticated, user, hasCheckedInit, getAccessTokenSilently]);

  const getAccessToken = async (): Promise<string | null> => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      });
    } catch (error) {
      console.error("Error getting access token:", error);
      return null;
    }
  };

  // Handle app loading state
  useEffect(() => {
    if (!auth0Loading) {
      setIsAppLoading(false);
    }
  }, [auth0Loading]);

  // Reset when user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setHasCheckedInit(false);
      setShowInitializer(false);
    }
  }, [isAuthenticated, user]);

  const loginWithRedirect = async (returnTo?: string) => {
    try {
      await auth0Login({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email",
          redirect_uri: window.location.origin + "/app/upload",
        },
        appState: returnTo ? { returnTo } : undefined,
      });
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin + "/",
      },
    });
  };

  const testAuth0Api = async () => {
    try {
      const result = await api.testAuth0();
      return result;
    } catch (error) {
      console.error("Auth0 API test error:", error);
      throw error;
    }
  };

  const handleInitializationComplete = () => {
    console.log("Auth0Context: Initialization completed");
    setShowInitializer(false);
  };

  const value = {
    user,
    isLoading: auth0Loading || isAppLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
    getAccessToken,
    testAuth0Api,
  };

  return (
    <Auth0Context.Provider value={value}>
      {showInitializer && (
        <UserInitializer onComplete={handleInitializationComplete} />
      )}
      {children}
    </Auth0Context.Provider>
  );
}

export function useAuth0Context() {
  const context = useContext(Auth0Context);
  if (context === undefined) {
    throw new Error(
      "useAuth0Context must be used within an Auth0ContextProvider"
    );
  }
  return context;
}
